import React, { useEffect, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useHistory, Link } from 'react-router-dom';
import moment from 'moment';
import { AlertTriangle } from 'lucide-react';
import { makeRESTApiRequest, ABORT_ERROR } from '../../../helpers/api';
import { ENDPOINTS, ORDER_STATUS_RANGE, getClientFacingOrderStatus } from '../../../helpers/constants';
import { formatCurrency, getBusinessDaysDiff, getFullAddress } from '../../../helpers/global';
import { getBgColorFromStatus } from '../../../helpers/ui';
import { OrderTypeToColor } from './OrgDashboard';
import Loader from '../../Loader';

const cardStyle = {
  background: '#fff',
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
  border: '1px solid #e2e8f0',
  padding: '18px 20px',
  height: '100%',
  minWidth: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column'
};

const ATTENTION_STATUS_STYLES = {
  'Link Sent': { bg: '#dbeafe', text: '#1d4ed8', label: 'Link Sent' },
  Hold: { bg: '#ffedd5', text: '#78350f', label: 'On Hold' }
};

const statusBadgeStyle = {
  borderRadius: '4px',
  display: 'inline-block',
  flexShrink: 0,
  fontSize: '0.6875rem',
  fontWeight: 700,
  lineHeight: 1.2,
  padding: '3px 7px',
  whiteSpace: 'nowrap'
};

const rowStyle = {
  alignItems: 'center',
  borderBottom: '1px solid #f1f5f9',
  display: 'flex',
  gap: '8px',
  minWidth: 0,
  padding: '8px 0'
};

const getClientStatusLabel = (status, cancellationRequested = false) => {
  if (status === 'Pending To Pay' && !cancellationRequested) return 'Completed/Unpaid';
  return getClientFacingOrderStatus(status, cancellationRequested);
};

const getStatusBadgeColors = (status, cancellationRequested = false) => {
  if (status === 'Hold' && !cancellationRequested) return getBgColorFromStatus('On Hold');
  return getBgColorFromStatus(
    status === 'Pending To Pay' && !cancellationRequested
      ? 'Completed/Unpaid'
      : getClientStatusLabel(status, cancellationRequested)
  );
};

const StatusBadge = ({ status, cancellationRequested = false, variant = 'default' }) => {
  if (variant === 'attention' && ATTENTION_STATUS_STYLES[status]) {
    const { bg, text, label } = ATTENTION_STATUS_STYLES[status];
    return (
      <span title={label} style={{ ...statusBadgeStyle, backgroundColor: bg, color: text }}>
        {label}
      </span>
    );
  }

  const statusLabel = getClientStatusLabel(status, cancellationRequested);
  const statusColors = getStatusBadgeColors(status, cancellationRequested);

  return (
    <span
      title={statusLabel}
      style={{
        ...statusBadgeStyle,
        backgroundColor: statusColors?.bg,
        color: statusColors?.text
      }}
    >
      {statusLabel === 'Completed/Unpaid' ? (
        <>
          Completed/<span style={{ color: '#dc3545' }}>Unpaid</span>
        </>
      ) : (
        statusLabel
      )}
    </span>
  );
};

const ServiceDot = ({ orderType }) => (
  <span
    aria-hidden
    style={{
      backgroundColor: OrderTypeToColor[orderType] || '#6B7280',
      borderRadius: '50%',
      flexShrink: 0,
      height: '8px',
      width: '8px'
    }}
  />
);

const PanelHeader = ({ title, onSeeAll, icon = null }) => (
  <div
    className="d-flex align-items-center justify-content-between mb-3"
    style={{ minHeight: '24px', minWidth: 0 }}
  >
    <div className="d-flex align-items-center" style={{ gap: '6px', minWidth: 0 }}>
      {icon}
      <h6 className="font-weight-bold mb-0" style={{ color: '#334155', fontSize: '0.875rem', lineHeight: 1.2 }}>
        {title}
      </h6>
    </div>
    {onSeeAll ? (
      <button
        type="button"
        className="btn btn-link btn-sm p-0"
        style={{ color: '#2563eb', flexShrink: 0, fontSize: '0.8rem', whiteSpace: 'nowrap' }}
        onClick={onSeeAll}
      >
        See All →
      </button>
    ) : null}
  </div>
);

const isClosingWithinFiveBusinessDays = closingDate => {
  if (!closingDate) return false;
  const today = moment().startOf('day');
  const closing = moment(closingDate).startOf('day');
  if (!closing.isValid() || closing.isBefore(today)) return false;
  return getBusinessDaysDiff(today, closing) <= 5;
};

const getServiceDetailUrl = order => {
  if (!order?.serviceId) return null;
  const params = new URLSearchParams();
  if (order.orderType) {
    params.set('OrderType', order.orderType);
  }
  const query = params.toString();
  return `/services/${order.serviceId}${query ? `?${query}` : ''}`;
};

// function OutstandingBalanceCard({ organizationIds = [] }) {
//   const [balanceData, setBalanceData] = useState({
//     total: 0,
//     due: 0,
//     overdue: 0
//   });
//   const [loading, setLoading] = useState(true);

//   useEffect(() => {
//     let alive = true;
//     const controller = new AbortController();

//     (async () => {
//       setLoading(true);
//       const { response, error } = await makeRESTApiRequest({
//         endpoint: ENDPOINTS.ORG_DASHBOARD_OUTSTANDING_INVOICE,
//         method: 'POST',
//         requestBody: { organizationIds },
//         signal: controller.signal
//       });

//       if (!alive) return;

//       if (error && error !== ABORT_ERROR) {
//         setBalanceData({ total: 0, due: 0, overdue: 0 });
//       } else if (response?.data) {
//         setBalanceData({
//           total: response.data.totalOutstanding || 0,
//           due: response.data.dueAmount || 0,
//           overdue: response.data.overdueAmount || 0
//         });
//       }
//       setLoading(false);
//     })();

//     return () => {
//       alive = false;
//       controller.abort();
//     };
//   }, [organizationIds?.length]);

//   const { total, due, overdue } = balanceData;

//   return (
//     <div style={cardStyle}>
//       <PanelHeader title="Outstanding Balance" />
//       {loading ? (
//         <div className="py-4 text-center">
//           <Loader width="100%" />
//         </div>
//       ) : (
//         <div className="flex-grow-1 d-flex flex-column">
//           <div
//             style={{
//               color: '#e15554',
//               fontFamily: "'Lato', sans-serif",
//               fontSize: '2.5rem',
//               fontWeight: 700,
//               lineHeight: 1,
//               marginBottom: '12px',
//               letterSpacing: '-0.03em'
//             }}
//           >
//             {formatCurrency(total, '$0.00')}
//           </div>
//           <div
//             className="d-flex flex-wrap"
//             style={{ color: '#64748b', fontSize: '1rem', gap: '16px', marginBottom: '20px' }}
//           >
//             <span style={{ fontSize: '0.8rem' }}>Due: {formatCurrency(due, '$0.00')}</span>
//             <span style={{ color: '#dc3545', fontSize: '0.8rem' }}>Overdue: {formatCurrency(overdue, '$0.00')}</span>
//           </div>
//           <Link
//             to="/statement"
//             className="btn btn-block mt-auto font-weight-bold"
//             style={{
//               backgroundColor: '#fbbf24',
//               border: 'none',
//               borderRadius: '8px',
//               color: '#111827',
//               fontSize: '0.875rem',
//               padding: '10px 16px',
//               textDecoration: 'none'
//             }}
//           >
//             View Statement
//           </Link>
//         </div>
//       )}
//     </div>
//   );
// }

function UpcomingClosingsPanel({ organizationIds }) {
  const history = useHistory();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();

    (async () => {
      setLoading(true);
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.ORG_DASHBOARD_UPCOMING_CLOSINGS,
        method: 'POST',
        requestBody: { organizationIds },
        signal: controller.signal
      });

      if (!alive) return;

      if (error && error !== ABORT_ERROR) {
        setOrders([]);
      } else {
        setOrders(response?.data || []);
      }
      setLoading(false);
    })();

    return () => {
      alive = false;
      controller.abort();
    };
  }, [organizationIds.length]);

  const seeAllUrl = `/services/pipeline?range=${ORDER_STATUS_RANGE.UPCOMING_CLOSINGS}&sortKey=service.closingDate&sortDirection=asc`;

  const handleRowClick = order => {
    const url = getServiceDetailUrl(order);
    if (url) history.push(url);
  };

  return (
    <div style={cardStyle}>
      <PanelHeader title="Upcoming Closings" onSeeAll={() => history.push(seeAllUrl)} />

      {loading ? (
        <div className="py-4 text-center">
          <Loader width="100%" />
        </div>
      ) : orders.length === 0 ? (
        <div className="text-muted py-4 text-center" style={{ fontSize: '0.875rem' }}>
          No upcoming closings
        </div>
      ) : (
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          {orders.map((order, index) => {
            const address = getFullAddress({
              propertyAddress: order.propertyAddress,
              city: order.city,
              state: order.state,
              zipCode: order.zipCode
            });
            const fileName = order.fileName?.trim();
            const displayLabel = `${fileName} - ${address}`;
            const showWarning = isClosingWithinFiveBusinessDays(order.closingDate);

            return (
              <div
                key={order._id}
                role="button"
                tabIndex={0}
                className="cursor-pointer"
                style={{
                  ...rowStyle,
                  alignItems: 'center',
                  borderBottom: index < orders.length - 1 ? rowStyle.borderBottom : 'none',
                  display: 'flex',
                  gap: '12px',
                  minWidth: 0,
                  overflow: 'hidden',
                  paddingBottom: index < orders.length - 1 ? '8px' : 0
                }}
                onClick={() => handleRowClick(order)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleRowClick(order);
                  }
                }}
              >
                <div
                  className="d-flex align-items-center"
                  style={{ gap: '8px', minWidth: 0, flex: 1, overflow: 'hidden' }}
                >
                  <ServiceDot orderType={order.orderType} />
                  <div
                    className="font-weight-normal"
                    title={displayLabel}
                    style={{
                      color: '#1e293b',
                      flex: '0 1 auto',
                      fontSize: '0.8125rem',
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {displayLabel}
                  </div>
                  <StatusBadge status={order.status} cancellationRequested={order.cancellationRequested} />
                </div>
                <div className="text-right" style={{ flexShrink: 0 }}>
                  <span
                    style={{
                      color: '#94a3b8',
                      display: 'block',
                      fontSize: '0.6875rem',
                      fontWeight: 500,
                      lineHeight: 1.2
                    }}
                  >
                    Close:
                  </span>
                  <div className="d-flex align-items-center justify-content-end" style={{ gap: '4px', marginTop: '2px' }}>
                    <span style={{ color: '#94a3b8', fontSize: '0.6875rem', fontWeight: 500, lineHeight: 1.2 }}>
                      {order.closingDate ? moment(order.closingDate).format('MM/DD/YYYY') : '—'}
                    </span>
                    {showWarning ? (
                      <OverlayTrigger
                        placement="bottom"
                        overlay={
                          <Tooltip id={`closing-warning-${order._id}`}>Closing within 5 business days</Tooltip>
                        }
                      >
                        <span
                          className="d-inline-flex align-items-center"
                          style={{ lineHeight: 0 }}
                          onClick={event => event.stopPropagation()}
                          onKeyDown={event => event.stopPropagation()}
                        >
                          <AlertTriangle size={14} style={{ color: '#f97316' }} aria-hidden />
                        </span>
                      </OverlayTrigger>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function OrdersNeedingAttentionPanel({ organizationIds }) {
  const history = useHistory();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();

    (async () => {
      setLoading(true);
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.ORG_DASHBOARD_ORDERS_NEEDING_ATTENTION,
        method: 'POST',
        requestBody: { organizationIds },
        signal: controller.signal
      });

      if (!alive) return;

      if (error && error !== ABORT_ERROR) {
        setOrders([]);
      } else {
        setOrders(response?.data || []);
      }
      setLoading(false);
    })();

    return () => {
      alive = false;
      controller.abort();
    };
  }, [organizationIds.length]);

  const seeAllUrl = `/services/pipeline?orderStatus=${encodeURIComponent('Link Sent')}&orderStatus=${encodeURIComponent(
    'Hold'
  )}`;

  const handleRowClick = order => {
    const url = getServiceDetailUrl(order);
    if (url) history.push(url);
  };

  return (
    <div style={cardStyle}>
      <PanelHeader
        title="Orders That Need Attention"
        onSeeAll={() => history.push(seeAllUrl)}
        icon={<AlertTriangle size={15} style={{ color: '#f97316', flexShrink: 0 }} aria-hidden />}
      />

      {loading ? (
        <div className="py-4 text-center">
          <Loader width="100%" />
        </div>
      ) : orders.length === 0 ? (
        <div className="text-muted py-4 text-center" style={{ fontSize: '0.875rem' }}>
          No orders need attention
        </div>
      ) : (
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          {orders.map((order, index) => {
            const address = getFullAddress({
              propertyAddress: order.propertyAddress,
              city: order.city,
              state: order.state,
              zipCode: order.zipCode
            });

            return (
              <div
                key={order._id}
                role="button"
                tabIndex={0}
                className="cursor-pointer"
                style={{
                  ...rowStyle,
                  alignItems: 'center',
                  borderBottom: index < orders.length - 1 ? rowStyle.borderBottom : 'none',
                  display: 'grid',
                  gap: '10px',
                  gridTemplateColumns: '8px minmax(0, 1fr) auto',
                  padding: '10px 0',
                  paddingBottom: index < orders.length - 1 ? '10px' : 0
                }}
                onClick={() => handleRowClick(order)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleRowClick(order);
                  }
                }}
              >
                <ServiceDot orderType={order.orderType} />
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    minWidth: 0,
                    overflow: 'hidden'
                  }}
                >
                  <span
                    className="font-weight-normal"
                    title={address}
                    style={{
                      color: '#1e293b',
                      flex: '1 1 45%',
                      fontSize: '0.8125rem',
                      lineHeight: 1.3,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {address || '—'}
                  </span>
                </div>
                <StatusBadge
                  status={order.status}
                  cancellationRequested={order.cancellationRequested}
                  variant="attention"
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// function RecentActivityPanel({ organizationIds }) {
//   const [events, setEvents] = useState([]);
//   const [loading, setLoading] = useState(true);

//   useEffect(() => {
//     let alive = true;
//     const controller = new AbortController();

//     (async () => {
//       setLoading(true);
//       const { response, error } = await makeRESTApiRequest({
//         endpoint: ENDPOINTS.ORG_DASHBOARD_RECENT_ACTIVITY,
//         method: 'POST',
//         requestBody: { organizationIds },
//         signal: controller.signal
//       });

//       if (!alive) return;

//       if (error && error !== ABORT_ERROR) {
//         setEvents([]);
//       } else {
//         setEvents(response?.data || []);
//       }
//       setLoading(false);
//     })();

//     return () => {
//       alive = false;
//       controller.abort();
//     };
//   }, [organizationIds.length]);

//   return (
//     <div className="mb-4" style={cardStyle}>
//       <h6 className="font-weight-bold mb-3" style={{ color: '#334155', fontSize: '0.875rem' }}>
//         Recent Activity
//       </h6>

//       {loading ? (
//         <div className="py-4 text-center">
//           <Loader width="100%" />
//         </div>
//       ) : events.length === 0 ? (
//         <div className="text-muted py-3 text-center" style={{ fontSize: '0.875rem' }}>
//           No recent activity
//         </div>
//       ) : (
//         <div>
//           {events.map((event, index) => {
//             const dotColor = getBgColorFromStatus(event.newStatus)?.bg;
//             return (
//               <div
//                 key={event._id}
//                 className="d-flex"
//                 style={{
//                   gap: '10px',
//                   padding: '10px 0',
//                   borderBottom: index < events.length - 1 ? '1px solid #f1f5f9' : 'none'
//                 }}
//               >
//                 <span
//                   aria-hidden
//                   style={{
//                     backgroundColor: dotColor,
//                     borderRadius: '50%',
//                     flexShrink: 0,
//                     height: '8px',
//                     marginTop: '6px',
//                     width: '8px'
//                   }}
//                 />
//                 <div style={{ minWidth: 0 }}>
//                   <div style={{ color: '#334155', fontSize: '0.8125rem', lineHeight: 1.4 }}>{event.text}</div>
//                   <div style={{ color: '#94a3b8', fontSize: '0.75rem', marginTop: '2px' }}>
//                     {moment(event.updatedAt).fromNow()}
//                   </div>
//                 </div>
//               </div>
//             )
//           })}
//         </div>
//       )}
//     </div>
//   );
// }

export default function ClientDashboardSummaryRow({ organizationIds = [], cardGap = 12 }) {
  const halfGap = cardGap / 2;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: `${cardGap}px` }}>
      <div
        className="row"
        style={{
          marginLeft: `-${halfGap}px`,
          marginRight: `-${halfGap}px`,
          rowGap: `${cardGap}px`
        }}
      >
        {/* <div className="col-12 col-lg-4" style={{ paddingLeft: halfGap, paddingRight: halfGap }}>
          <OutstandingBalanceCard organizationIds={organizationIds} />
        </div> */}
        <div className="col-12 col-lg-6" style={{ paddingLeft: halfGap, paddingRight: halfGap }}>
          <OrdersNeedingAttentionPanel organizationIds={organizationIds} />
        </div>
        <div className="col-12 col-lg-6" style={{ paddingLeft: halfGap, paddingRight: halfGap }}>
          <UpcomingClosingsPanel organizationIds={organizationIds} />
        </div>
      </div>
      {/* <RecentActivityPanel organizationIds={organizationIds} /> */}
    </div>
  );
}
